import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useIssueState} from '@github-ui/issue-pr-state'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {ActionList} from '@primer/react'
import type React from 'react'
import {graphql, useFragment} from 'react-relay'

import {useCachePreheatIssueViewer} from '../../../hooks/use-cache-preheat-issueviewer'
import type {DependencyIssueFragment$key} from './__generated__/DependencyIssueFragment.graphql'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const DependencyIssueFragment = graphql`
  fragment DependencyIssueFragment on Issue {
    title
    titleHTML
    url
    number
    repository {
      nameWithOwner
      name
      owner {
        login
      }
    }
    state
    stateReason
  }
`

type DependencyIssueProps = {
  onLinkClick?: (event: MouseEvent) => void
}

type DependencyIssueInternalProps = Omit<DependencyIssueProps, 'queryRef'> & {
  issueKey: DependencyIssueFragment$key
}

export function DependencyIssue({issueKey, onLinkClick}: DependencyIssueInternalProps) {
  const data = useFragment(DependencyIssueFragment, issueKey)

  useCachePreheatIssueViewer({
    owner: data.repository.owner.login,
    repo: data.repository.name,
    number: data.number,
    source: 'DependencyIssue',
    tabbedTimeline: isFeatureEnabled('issues_react_timeline_tabs'),
  })

  // TODO: Add subscription for dependency issues
  // useDependencyIssueSubscription(data.id)
  const {sourceIcon} = useIssueState({
    state: data.state,
    stateReason: data.stateReason,
  })

  const IconComponent = sourceIcon('Issue')
  /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side */
  const title = (data.titleHTML || data.title) as SafeHTMLString

  return (
    <ActionList.LinkItem
      href={data.url}
      target="_blank"
      onClick={
        onLinkClick
          ? (event: React.MouseEvent<HTMLElement>) => {
              onLinkClick(event.nativeEvent)
            }
          : undefined
      }
    >
      <ActionList.LeadingVisual>
        {/* eslint-disable-next-line react-hooks/static-components */}
        <IconComponent />
      </ActionList.LeadingVisual>
      <VerifiedHTMLText html={title} className="markdown-title" />
      <ActionList.Description variant="block">
        {data.repository.nameWithOwner}#{data.number}
      </ActionList.Description>
    </ActionList.LinkItem>
  )
}
